import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  User,
  Truck,
  CreditCard,
  Clock,
  Star,
  Phone,
  Navigation,
} from 'lucide-react';
import { formatCurrency, formatDate, formatPhone } from '@/lib/utils';
import { ROUTES, buildRoute } from '@/constants/routes';
import { useBooking } from '../hooks/useBookings';
import { PageHeader } from '@/components/common/PageHeader';
import { BookingStatusBadge, PaymentStatusBadge } from '@/components/common/StatusBadge';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageSpinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/ui/empty-state';

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: booking, isLoading } = useBooking(id!);

  if (isLoading) {
    return <PageSpinner message="Loading booking details..." />;
  }

  if (!booking) {
    return (
      <EmptyState
        icon="bookings"
        title="Booking not found"
        description="The booking you're looking for doesn't exist or has been removed."
        action={{ label: 'Go back', onClick: () => navigate(ROUTES.BOOKINGS) }}
      />
    );
  }

  const canTrack = ['driver_assigned', 'ongoing'].includes(booking.status);

  return (
    <div className="space-y-6">
      <PageHeader
        title={booking.bookingNumber}
        breadcrumbs={[
          { label: 'Bookings', href: ROUTES.BOOKINGS },
          { label: booking.bookingNumber },
        ]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(ROUTES.BOOKINGS)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {canTrack && (
              <Button onClick={() => navigate(buildRoute(ROUTES.LIVE_TRACKING, { id: booking.id }))}>
                <Navigation className="h-4 w-4 mr-2" />
                Track Live
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <BookingStatusBadge status={booking.status} />
                  <span className="text-muted-foreground">
                    Created {formatDate(booking.createdAt, 'datetime')}
                  </span>
                </div>
                {booking.rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-5 w-5 text-warning fill-warning" />
                    <span className="font-medium">{booking.rating}/5</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Route Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Route Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="h-3 w-3 rounded-full bg-success" />
                    <div className="w-0.5 h-16 bg-border" />
                    <div className="h-3 w-3 rounded-full bg-destructive" />
                  </div>
                  <div className="flex-1 space-y-6">
                    <div>
                      <p className="text-sm text-muted-foreground">Pickup</p>
                      <p className="font-medium">{booking.pickup.address}</p>
                      <p className="text-sm text-muted-foreground">
                        {booking.pickup.city}, {booking.pickup.state} - {booking.pickup.pincode}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Dropoff</p>
                      <p className="font-medium">{booking.dropoff.address}</p>
                      <p className="text-sm text-muted-foreground">
                        {booking.dropoff.city}, {booking.dropoff.state} - {booking.dropoff.pincode}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Distance</p>
                    <p className="font-medium">{booking.distance} km</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Duration</p>
                    <p className="font-medium">{booking.duration} min</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Vehicle</p>
                    <p className="font-medium capitalize">{booking.vehicleType.replace('_', ' ')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <TimelineItem
                  label="Booking Created"
                  time={booking.createdAt}
                  completed
                />
                <TimelineItem
                  label="Booking Confirmed"
                  time={booking.confirmedAt}
                  completed={!!booking.confirmedAt}
                />
                <TimelineItem
                  label="Picked Up"
                  time={booking.pickedUpAt}
                  completed={!!booking.pickedUpAt}
                />
                <TimelineItem
                  label="Delivered"
                  time={booking.deliveredAt}
                  completed={!!booking.deliveredAt}
                />
                {booking.cancelledAt && (
                  <TimelineItem
                    label="Cancelled"
                    time={booking.cancelledAt}
                    completed
                    variant="destructive"
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                Customer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Avatar src={booking.customer.avatar} alt={booking.customer.name} size="lg" />
                <div>
                  <p className="font-medium">{booking.customer.name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {formatPhone(booking.customer.phone)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Driver Card */}
          {booking.driver && (
            <Card
              className={booking.driver.profileId ? "cursor-pointer hover:shadow-md hover:border-primary/40 transition-all duration-200" : ""}
              onClick={() => booking.driver?.profileId && navigate(buildRoute(ROUTES.DRIVER_DETAIL, { id: booking.driver.profileId! }))}
            >
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Driver
                  {booking.driver.profileId && (
                    <span className="ml-auto text-xs font-normal text-primary">View profile →</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <Avatar src={booking.driver.avatar} alt={booking.driver.name} size="lg" />
                  <div>
                    <p className="font-medium">{booking.driver.name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {formatPhone(booking.driver.phone)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {booking.driver.vehicleNumber}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <PaymentStatusBadge status={booking.payment.status} />
                <Badge
                  variant={booking.payment.method === 'cash' ? 'secondary' : 'outline'}
                  className="capitalize font-medium"
                >
                  {booking.payment.method === 'cash' ? '💵 Cash' : booking.payment.method === 'upi' ? '📱 UPI' : booking.payment.method}
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated Fare</span>
                  <span>{formatCurrency(parseFloat(booking.fare.total?.toString() || '0'))}</span>
                </div>
                {booking.fare.surge && (
                  <div className="flex justify-between text-warning">
                    <span>Surge</span>
                    <span>+{formatCurrency(booking.fare.surge)}</span>
                  </div>
                )}
                {booking.fare.discount && (
                  <div className="flex justify-between text-success">
                    <span>Discount</span>
                    <span>-{formatCurrency(booking.fare.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold pt-2 border-t text-base">
                  <span>Total</span>
                  <span>{formatCurrency(booking.fare.total)}</span>
                </div>
                {booking.payment.transactionId && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-1">
                    <span>Transaction ID</span>
                    <span className="font-mono">{booking.payment.transactionId}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function TimelineItem({
  label,
  time,
  completed,
  variant = 'default',
}: {
  label: string;
  time?: string;
  completed: boolean;
  variant?: 'default' | 'destructive';
}) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`h-3 w-3 rounded-full ${
          completed
            ? variant === 'destructive'
              ? 'bg-destructive'
              : 'bg-success'
            : 'bg-muted'
        }`}
      />
      <div className="flex-1 flex items-center justify-between">
        <span className={completed ? 'font-medium' : 'text-muted-foreground'}>
          {label}
        </span>
        {time && (
          <span className="text-sm text-muted-foreground">
            {formatDate(time, 'datetime')}
          </span>
        )}
      </div>
    </div>
  );
}
